/**
 * TripBudgetCard — Trip-only Phase 2 (iter171).
 * Shows budget vs actual per category with a stacked progress visualisation
 * and over-budget warnings. Refreshes when SSE-driven `group` updates land,
 * since we re-fetch the report on each ctx.group change.
 */
import { useEffect, useState, useCallback } from 'react';
import { Wallet, AlertTriangle, Sparkles } from 'lucide-react';
import api from '../lib/api';

export default function TripBudgetCard({ ctx }) {
  const groupId = ctx?.group?.id;
  const isTrip = (ctx?.group?.group_type || '').toLowerCase() === 'trip';
  const hasBudget = Array.isArray(ctx?.group?.trip_budget) && ctx.group.trip_budget.length > 0;
  const formatCurrency = ctx?.formatCurrency || ((v) => `\u20b9${Math.round(v || 0).toLocaleString('en-IN')}`);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!groupId || !isTrip || !hasBudget) return;
    setLoading(true);
    try {
      const res = await api.get(`/groups/${groupId}/trip-budget-report`);
      setReport(res.data);
    } catch {
      // silent — card just won't render
    } finally {
      setLoading(false);
    }
  }, [groupId, isTrip, hasBudget]);

  useEffect(() => { fetchReport(); }, [fetchReport, ctx?.group?.fund_balance, ctx?.group?.coin_balances]);

  if (!isTrip || !hasBudget) return null;
  if (loading && !report) return null;
  if (!report) return null;

  const totalBudget = report.total_budget || 0;
  const totalActual = report.total_actual || 0;
  const totalPct = report.total_percent || 0;
  const overTotal = !!report.over_total;

  return (
    <div className="rounded-2xl border-2 border-border bg-card p-4 sm:p-5 space-y-4" data-testid="trip-budget-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Wallet className="w-3 h-3" /> Trip Budget
          </p>
          <p className="font-display text-2xl font-bold mt-0.5 amount-display" data-testid="budget-total-actual">
            {formatCurrency(totalActual)}
            <span className="text-sm font-normal text-muted-foreground"> / {formatCurrency(totalBudget)}</span>
          </p>
        </div>
        <div className={`text-right ${overTotal ? 'text-rose-600' : 'text-muted-foreground'}`}>
          <p className="text-xs font-bold uppercase tracking-widest">{overTotal ? 'OVER' : 'Used'}</p>
          <p className="text-xl font-bold tabular-nums" data-testid="budget-total-percent">{totalPct}%</p>
        </div>
      </div>

      {/* Total bar */}
      <div className="h-2.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${overTotal ? 'bg-rose-500' : 'bg-primary'}`}
          style={{ width: `${Math.min(100, totalPct)}%` }}
          data-testid="budget-total-bar"
        />
      </div>

      {/* Per-category rows */}
      <div className="space-y-2.5">
        {(report.items || []).map((it) => {
          const pct = Math.min(100, it.percent || 0);
          const over = it.over;
          return (
            <div key={it.key} data-testid={`budget-item-${it.key}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs font-semibold truncate flex items-center gap-1">
                  {it.label}
                  {over && <AlertTriangle className="w-3 h-3 text-rose-500 shrink-0" />}
                </p>
                <p className={`text-xs tabular-nums ${over ? 'text-rose-600 font-bold' : 'text-muted-foreground'}`}>
                  {formatCurrency(it.actual)} <span className="opacity-60">/ {formatCurrency(it.budget)}</span>
                </p>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${over ? 'bg-rose-500' : 'bg-primary/60'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Unmatched bucket */}
      {report.unmatched && report.unmatched.actual > 0 && (
        <div
          className="text-xs rounded-xl bg-amber-500/10 border border-amber-500/30 p-2 leading-snug flex items-start gap-2"
          data-testid="budget-unmatched"
        >
          <Sparkles className="w-3 h-3 mt-0.5 text-amber-600 shrink-0" />
          <div>
            <span className="font-bold text-amber-700 dark:text-amber-400">
              {formatCurrency(report.unmatched.actual)}
            </span>{' '}
            in approved expenses fell outside your budget categories.
            {' '}
            <span className="opacity-80">
              ({Object.entries(report.unmatched.breakdown).map(([c, v]) => `${c}: ${formatCurrency(v)}`).join(' · ')})
            </span>
            {' '}Edit categories in Group Settings → Trip Budget.
          </div>
        </div>
      )}
    </div>
  );
}
